import React, {useEffect, useState} from 'react';
import {Award, Clock, DollarSign, DownloadIcon, Edit2, Package, Plus, Save, Tag, Trash2, X} from 'lucide-react';
import {useCatalog} from "./useCatalog";
import AddItemModal from "./components/addItemModal/addItemModal";
import ManageCategoriesModal from "./components/manageCategoryModal/manageCategoryModal";
import BulkCsvUpload from "../components/BulkCsvUpload";
import { getBusinessServices } from "app/redux/businessServicesSlice";
import { getProducts } from "../redux/productSlice";
import {useDispatch} from "react-redux";

interface Service {
    id: string;
    name: string;
    description: string;
    category: string;
    duration: number;
    price: number;
    type: 'service' | 'product';
    notes?: string;
    // Each provider's effective price (override or base) for this service
    providerPrices?: { userId: number; providerName: string; price: string; isOverride: boolean }[];
}

interface Product {
    id: string;
    name: string;
    description: string;
    category: string;
    price: number;
    stock: number;
    type: 'service' | 'product';
}

type CatalogItem = Service | Product;


const Catalog: React.FC = () => {

    const {
        handleEditItem,
        setEditingItem,
        currentItems,
        services,
        products,
        categories,
        getCategoryColor,
        handleDeleteItem,
        activeTab,
        setActiveTab,
        showAddModal,
        setShowAddModal,
        showViewModal,
        showBulkUploadModal,
        setShowViewModal,
        setShowBulkUploadModal,
        showCategoriesModal,
        setShowCategoriesModal,
        selectedItem,
        setSelectedItem,
        editingItem,
        // managedCategories,
        // setManagedCategories
    } = useCatalog()
    const dispatch = useDispatch();
    const reloadCatalog = () => {
        if (activeTab === "services") {
            dispatch(getBusinessServices() as any);
        } else {
            dispatch(getProducts() as any);
        }
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <main className="px-6 py-8">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Catalog</h2>
                </div>

                {/* Tabs and Actions */}
                <div className="flex mobile:flex-col mobile:gap-2 w-full items-center justify-between mb-6">
                    <div className="flex space-x-6 border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('services')}
                            className={`pb-3 px-1 text-sm font-medium ${
                                activeTab === 'services'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Services
                        </button>
                        <button
                            onClick={() => setActiveTab('products')}
                            className={`pb-3 px-1 text-sm font-medium ${
                                activeTab === 'products'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            Products
                        </button>
                    </div>

                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => setShowCategoriesModal(true)}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                            Manage Categories
                        </button>
                        <button
                            onClick={() => setShowBulkUploadModal(true)}
                            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <DownloadIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">Import</span>
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4 mr-2"/>
                            Add
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {activeTab === 'services' ? 'Service' : 'Product'}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Category
                            </th>
                            {activeTab === 'services' ? (
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Duration
                                </th>
                            ) : (
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Stock
                                </th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Price
                            </th>
                            {activeTab === 'services' && (
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Notes
                                </th>
                            )}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {currentItems?.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{item?.name}</div>
                                        <div className="text-sm text-gray-500">{item?.description}</div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                    <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(item?.category)}`}>
                      {item?.category}
                    </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                    {activeTab === 'services' ? (
                                        <div>
                                            {(item as unknown as Service)?.duration} min
                                        </div>
                                    ) : (
                                        <div>
                                            {(item as unknown as Product)?.stock} units
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                    <div>
                                        ${item?.price}
                                    </div>
                                    {/* Per-provider pricing: each provider's effective price (bold = custom override) */}
                                    {activeTab === 'services' && ((item as unknown as Service)?.providerPrices?.length ?? 0) > 0 && (
                                        <div className="mt-1 space-y-0.5">
                                            {(item as unknown as Service).providerPrices!.map((pp) => (
                                                <div key={pp.userId} className="text-xs text-gray-500">
                                                    {pp.providerName}:{" "}
                                                    <span className={pp.isOverride ? "font-semibold text-gray-900" : ""}>
                                                        {pp.price}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </td>
                                {activeTab === 'services' && (
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px]">
                                        <div className="flex items-center space-x-1 group relative">
                                            <span className="truncate max-w-[150px] inline-block">
                                                {(item as unknown as Service)?.notes || "-"}
                                            </span>
                                            {(item as unknown as Service)?.notes && (
                                                <div className="relative">
                                                    <button type="button" className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full border border-gray-300">
                                                        i
                                                    </button>
                                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-50 pointer-events-none whitespace-normal break-words">
                                                        {(item as unknown as Service)?.notes}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                )}
                                <td className="px-6 py-4 text-sm font-medium">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => {
                                                setSelectedItem(item);
                                                setShowViewModal(true);
                                            }}
                                            className="text-gray-400 hover:text-blue-600"
                                        >
                                            <Edit2 className="w-4 h-4"/>
                                        </button>
                                        <button
                                            onClick={() => handleDeleteItem(item?.id, item?.type)}
                                            className="text-gray-400 hover:text-red-600"
                                        >
                                            <Trash2 className="w-4 h-4"/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </main>

            {/* Add Modal */}
            {showAddModal && (
                <AddItemModal
                    onClose={() => setShowAddModal(false)}
                    // onAdd={handleAddItem}
                    categories={categories}
                />
            )}
            {showBulkUploadModal && (activeTab === 'services' ? (
                <BulkCsvUpload
                    title="Services"
                    endpoint="/v1/services/bulk"
                    csvHeaders={[
                        "title",
                        "description",
                        "duration",
                        "price_cents",
                        "service_category",
                    ]}
                    sampleRow={{
                        title: "Women's Cut",
                        description: "Women's haircut",
                        duration: 60,
                        price_cents: 5000,
                        service_category: "Hair",
                    }}
                    onClose={() => setShowBulkUploadModal(false)}
                    onSuccess={reloadCatalog}
                />
            ) :  (
                <BulkCsvUpload
                title="Products"
                endpoint="/v1/products/bulk"
                csvHeaders={[
                    "title",
                    "description",
                    "price_cents",
                    "stock",
                    "product_category",
                ]}
                sampleRow={{
                    title: "Hair Color",
                    description: "Hair color",
                    price_cents: 1000,
                    stock: 50,
                    product_category: "Hair Products",
                }}
                onClose={() => setShowBulkUploadModal(false)}
                onSuccess={reloadCatalog}
            />))}

            {/* Manage Categories Modal */}
            {showCategoriesModal && (
                <ManageCategoriesModal
                    categories={categories}
                    onClose={() => setShowCategoriesModal(false)}
                    onUpdate={()=>{}}
                />
            )}

            {/* View/Edit Modal */}
            {showViewModal && selectedItem && (
                <ViewItemModal
                    item={selectedItem}
                    onClose={() => {
                        setShowViewModal(false);
                        setSelectedItem(null);
                        setEditingItem(null);
                    }}
                    onEdit={handleEditItem}
                    isEditing={editingItem?.id === selectedItem.id}
                    onStartEdit={() => setEditingItem(selectedItem)}
                    onCancelEdit={() => setEditingItem(null)}
                    categories={categories}
                />
            )}
        </div>
    );
}


// interface ViewItemModalProps {
//     item: CatalogItem;
//     onClose: () => void;
//     onEdit: (item: CatalogItem) => void;
//     isEditing: boolean;
//     onStartEdit: () => void;
//     onCancelEdit: () => void;
//     categories: typeof categories;
// }

function ViewItemModal({item, onClose, onEdit, isEditing, onStartEdit, onCancelEdit, categories}: any) {
    const [editData, setEditData] = useState(item);

    const handleSave = () => {
        onEdit(editData);
        onClose()
    };

    const getCategoryColor = (category: string) => {
        const colors: { [key: string]: string } = {
            'Injectables': 'bg-blue-100 text-blue-800',
            'Skin Treatments': 'bg-green-100 text-green-800',
            'Laser Treatments': 'bg-purple-100 text-purple-800',
            'Body Contouring': 'bg-pink-100 text-pink-800',
            'Facial Treatments': 'bg-orange-100 text-orange-800',
            'Skincare': 'bg-cyan-100 text-cyan-800',
            'Supplements': 'bg-yellow-100 text-yellow-800',
            'Tools': 'bg-gray-100 text-gray-800',
            'Accessories': 'bg-indigo-100 text-indigo-800'
        };
        return colors[category] || 'bg-gray-100 text-gray-800';
    };

    const getTypeIcon = () => {
        return item?.type === 'service' ? (
            <div
                className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                <Award className="w-4 h-4 text-white"/>
            </div>
        ) : (
            <div
                className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-sm">
                <Package className="w-4 h-4 text-white"/>
            </div>
        );
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-lg mx-4 shadow-lg">
                {/* Header with gradient background */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {getTypeIcon()}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {isEditing ? 'Edit' : ''} {item?.type === 'service' ? 'Service' : 'Product'} Details
                                </h3>
                                <p className="text-xs text-gray-600">
                                    {item?.type === 'service' ? 'Professional treatment service' : 'Retail product information'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {!isEditing ? (
                                <button
                                    onClick={onStartEdit}
                                    className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                                >
                                    <Edit2 className="w-4 h-4 mr-2"/>
                                    Edit
                                </button>
                            ) : (
                                <div className="flex space-x-2">
                                    <button
                                        onClick={handleSave}
                                        className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                                    >
                                        <Save className="w-4 h-4 mr-2"/>
                                        Save Changes
                                    </button>
                                    <button
                                        onClick={onCancelEdit}
                                        className="px-3 py-1.5 text-gray-600 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                            <button onClick={onClose}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
                                <X className="w-4 h-4"/>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content with improved layout */}
                <div className="p-4 space-y-4">
                    {/* Name Section with enhanced styling */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                            Name
                        </label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={editData.name}
                                onChange={(e) => setEditData({...editData, name: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        ) : (
                            <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-500">
                                <p className="text-lg font-semibold text-gray-900">{item?.name}</p>
                            </div>
                        )}
                    </div>

                    {/* Description Section with card styling */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                            Description
                        </label>
                        {isEditing ? (
                            <textarea
                                value={editData.description}
                                onChange={(e) => setEditData({...editData, description: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                rows={2}
                            />
                        ) : (
                            <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm text-gray-700">{item?.description}</p>
                            </div>
                        )}
                    </div>

                    {item?.type === 'service' && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                                Notes
                            </label>
                            {isEditing ? (
                                <textarea
                                    value={editData.notes}
                                    onChange={(e) => setEditData({...editData, notes: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                    rows={2}
                                    placeholder="Enter service notes"
                                />
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <p className="text-sm text-gray-700">{item?.notes || "No notes available"}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Category Section with enhanced badge */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                            Category
                        </label>
                        {isEditing ? (
                            <select
                                value={editData.category.id}
                                onChange={(e) => setEditData({...editData, category: +e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            >
                                {categories[item?.type].map((cat: any) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        ) : (
                            <div className="flex items-center">
                <span
                    className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-lg ${getCategoryColor(item?.category)}`}>
                  <Tag className="w-4 h-4 mr-2"/>
                    {item?.category}
                </span>
                            </div>
                        )}
                    </div>

                    {/* Stats Section with card layout */}
                    <div className="grid grid-cols-2 gap-3">
                        {item?.type === 'service' ? (
                            <div
                                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                                <label className="block text-xs font-medium text-blue-800 mb-2 uppercase tracking-wide">
                                    Duration
                                </label>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={(editData as Service)?.duration}
                                        onChange={(e) => setEditData({
                                            ...editData,
                                            duration: parseInt(e.target.value)
                                        } as Service)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        min="1"
                                    />
                                ) : (
                                    <div className="flex items-center">
                                        <div
                                            className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center mr-2">
                                            <Clock className="w-4 h-4 text-white"/>
                                        </div>
                                        <div>
                                            <p className="text-lg font-semibold text-blue-900">{(item as Service)?.duration}</p>
                                            <p className="text-xs text-blue-700">minutes</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div
                                className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                                <label
                                    className="block text-xs font-medium text-green-800 mb-2 uppercase tracking-wide">
                                    Stock
                                </label>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        value={(editData as Product).stock}
                                        onChange={(e) => setEditData({
                                            ...editData,
                                            stock: parseInt(e.target.value)
                                        } as Product)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        min="0"
                                    />
                                ) : (
                                    <div className="flex items-center">
                                        <div
                                            className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center mr-2">
                                            <Package className="w-4 h-4 text-white"/>
                                        </div>
                                        <div>
                                            <p className="text-lg font-semibold text-green-900">{(item as Product).stock}</p>
                                            <p className="text-xs text-green-700">units available</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div
                            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
                            <label className="block text-xs font-medium text-purple-800 mb-2 uppercase tracking-wide">
                                Price
                            </label>
                            {isEditing ? (
                                <input
                                    type="number"
                                    value={editData.price}
                                    onChange={(e) => setEditData({...editData, price: parseFloat(e.target.value)})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    min="0"
                                    step="0.01"
                                />
                            ) : (
                                <div className="flex items-center">
                                    <div
                                        className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center mr-2">
                                        <DollarSign className="w-4 h-4 text-white"/>
                                    </div>
                                    <div>
                                        <p className="text-lg font-semibold text-purple-900">${item?.price}</p>
                                        <p className="text-xs text-purple-700">per {item?.type}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

//

export default Catalog;
